import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './HOTshirtOrders.css';

function HOTshirtOrders({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [filterFranchisee, setFilterFranchisee] = useState('all');
  const [filterIncluded, setFilterIncluded] = useState('all');
  const [franchiseeList, setFranchiseeList] = useState([]);

  // Supplier Batch state
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [batchForm, setBatchForm] = useState({ name: '', startDate: '', endDate: '' });
  const [savingBatch, setSavingBatch] = useState(false);

  // Packing Report state
  const [showPackingModal, setShowPackingModal] = useState(false);
  const [selectedPackingBatches, setSelectedPackingBatches] = useState([]);

  const allSizes = [
    '2-3 YEARS', '4-5 YEARS', '5-6 YEARS', '7-8 YEARS',
    '9-10 YEARS', '11-12 YEARS', '13-14 YEARS',
    'S', 'M', 'L', 'XL', 'XXL', 'XXXL'
  ];

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([loadOrders(), loadBatches()]);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── BATCH FUNCTIONS ──────────────────────────────────────────────

  const loadBatches = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'supplierBatches'));
      const batchList = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          startDateParsed: data.startDate?.toDate ? data.startDate.toDate() : new Date(data.startDate),
          endDateParsed: data.endDate?.toDate ? data.endDate.toDate() : new Date(data.endDate),
        };
      });

      // Sort by startDate ascending
      batchList.sort((a, b) => a.startDateParsed - b.startDateParsed);
      setBatches(batchList);
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  // Simple date range check — does this order fall within this batch's dates?
  const isOrderInBatch = (order, batch) => {
    if (!order.orderDateParsed) return false;

    const orderTime = order.orderDateParsed.getTime();
    const start = new Date(batch.startDateParsed);
    start.setHours(0, 0, 0, 0);
    const end = new Date(batch.endDateParsed);
    end.setHours(23, 59, 59, 999);

    return orderTime >= start.getTime() && orderTime <= end.getTime();
  };

  // Find which batch label to show on an order card (for "All Orders" view)
  const getOrderBatchLabel = (order) => {
    if (!order.orderDateParsed || batches.length === 0) return null;
    for (const batch of batches) {
      if (isOrderInBatch(order, batch)) return batch.name;
    }
    return null;
  };

  const getBatchOrderCounts = () => {
    const counts = {};
    batches.forEach(batch => {
      counts[batch.id] = orders.filter(order => isOrderInBatch(order, batch)).length;
    });
    return counts;
  };

  const handleCreateBatch = () => {
    // Auto-suggest next dates
    let suggestedStart = '';
    let suggestedEnd = '';
    let suggestedName = '';

    if (batches.length > 0) {
      const lastBatch = batches[batches.length - 1];
      const nextStart = new Date(lastBatch.endDateParsed);
      nextStart.setDate(nextStart.getDate() + 1);
      const nextEnd = new Date(nextStart);
      nextEnd.setDate(nextEnd.getDate() + 6);

      suggestedStart = nextStart.toISOString().split('T')[0];
      suggestedEnd = nextEnd.toISOString().split('T')[0];
      suggestedName = `Supplier Order ${batches.length + 1}`;
    } else {
      suggestedName = 'Supplier Order 1';
    }

    setBatchForm({
      name: suggestedName,
      startDate: suggestedStart,
      endDate: suggestedEnd
    });
    setEditingBatch(null);
    setShowBatchModal(true);
  };

  const handleEditBatch = (batch) => {
    setBatchForm({
      name: batch.name || '',
      startDate: batch.startDateParsed.toISOString().split('T')[0],
      endDate: batch.endDateParsed.toISOString().split('T')[0]
    });
    setEditingBatch(batch);
    setShowBatchModal(true);
  };

  const handleSaveBatch = async () => {
    if (!batchForm.name || !batchForm.startDate || !batchForm.endDate) {
      alert('Please fill in all fields.');
      return;
    }

    if (new Date(batchForm.endDate) < new Date(batchForm.startDate)) {
      alert('End date must be after start date.');
      return;
    }

    setSavingBatch(true);
    try {
      const batchData = {
        name: batchForm.name.trim(),
        startDate: Timestamp.fromDate(new Date(batchForm.startDate)),
        endDate: Timestamp.fromDate(new Date(batchForm.endDate)),
        status: 'open',
        updatedAt: new Date()
      };

      if (editingBatch) {
        await updateDoc(doc(db, 'supplierBatches', editingBatch.id), batchData);
      } else {
        batchData.createdAt = new Date();
        await addDoc(collection(db, 'supplierBatches'), batchData);
      }

      setShowBatchModal(false);
      setEditingBatch(null);
      await loadBatches();
    } catch (error) {
      console.error('Error saving batch:', error);
      alert('Error saving batch: ' + error.message);
    } finally {
      setSavingBatch(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Delete this supplier batch? (Orders will not be affected)')) return;

    try {
      await deleteDoc(doc(db, 'supplierBatches', batchId));
      if (selectedBatch === batchId) setSelectedBatch('all');
      await loadBatches();
    } catch (error) {
      console.error('Error deleting batch:', error);
      alert('Error deleting batch: ' + error.message);
    }
  };

  const handleToggleBatchStatus = async (batch) => {
    const newStatus = batch.status === 'ordered' ? 'open' : 'ordered';
    try {
      await updateDoc(doc(db, 'supplierBatches', batch.id), {
        status: newStatus,
        updatedAt: new Date()
      });
      await loadBatches();
    } catch (error) {
      console.error('Error updating batch status:', error);
    }
  };

  // ─── ORDER LOADING ────────────────────────────────────────────────

  const loadOrders = async () => {
    try {
      const ordersSnapshot = await getDocs(collection(db, 'tshirtOrders'));

      const ordersList = ordersSnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const orderDate = data.orderDate?.toDate ? data.orderDate.toDate() :
                         data.orderDate ? new Date(data.orderDate) : null;
        return {
          id: docSnap.id,
          ...data,
          orderDateParsed: orderDate
        };
      });

      ordersList.sort((a, b) => {
        const dateA = a.orderDateParsed || new Date(0);
        const dateB = b.orderDateParsed || new Date(0);
        return dateB - dateA;
      });

      setOrders(ordersList);

      const franchisees = [...new Set(ordersList.map(o => o.franchiseeName).filter(Boolean))];
      franchisees.sort();
      setFranchiseeList(franchisees);
    } catch (error) {
      console.error('Error loading t-shirt orders:', error);
      throw error;
    }
  };

  // ─── FILTERING ────────────────────────────────────────────────────

  const getFilteredOrders = () => {
    return orders.filter(order => {
      // Batch filter — show orders that fall within the selected batch's date range
      if (selectedBatch !== 'all') {
        const batch = batches.find(b => b.id === selectedBatch);
        if (batch && !isOrderInBatch(order, batch)) return false;
      }
      // Franchisee filter
      if (filterFranchisee !== 'all' && order.franchiseeName !== filterFranchisee) return false;
      // Month-end filter
      if (filterIncluded === 'yes' && order.includeInMonthEnd !== true) return false;
      if (filterIncluded === 'no' && order.includeInMonthEnd !== false) return false;
      return true;
    });
  };

  // ─── SIZE SUMMARIES ───────────────────────────────────────────────

  const getSizeSummary = (filteredOrders) => {
    const summary = {};
    allSizes.forEach(size => { summary[size] = 0; });

    filteredOrders.forEach(order => {
      if (order.studentTshirts && Array.isArray(order.studentTshirts)) {
        order.studentTshirts.forEach(st => {
          if (st.size && summary[st.size] !== undefined) {
            summary[st.size] += 1;
          }
        });
      }
      if (order.extraStock) {
        Object.entries(order.extraStock).forEach(([size, qty]) => {
          if (summary[size] !== undefined) {
            summary[size] += (qty || 0);
          }
        });
      }
    });

    return summary;
  };

  const getAdultSummary = (filteredOrders) => {
    let smallToLarge = 0;
    let xlToXxxxl = 0;
    let stickerRolls = 0;
    const vneckSizes = { S: 0, M: 0, L: 0, XL: 0, XXL: 0, XXXL: 0 };

    filteredOrders.forEach(order => {
      if (order.adultTshirts) {
        smallToLarge += order.adultTshirts.smallToLarge?.quantity || 0;
        xlToXxxxl += order.adultTshirts.xlToXxxxl?.quantity || 0;
        if (order.adultTshirts.smallToLarge?.sizes) {
          vneckSizes.S += order.adultTshirts.smallToLarge.sizes.S || 0;
          vneckSizes.M += order.adultTshirts.smallToLarge.sizes.M || 0;
          vneckSizes.L += order.adultTshirts.smallToLarge.sizes.L || 0;
        }
        if (order.adultTshirts.xlToXxxxl?.sizes) {
          vneckSizes.XL += order.adultTshirts.xlToXxxxl.sizes.XL || 0;
          vneckSizes.XXL += order.adultTshirts.xlToXxxxl.sizes.XXL || 0;
          vneckSizes.XXXL += order.adultTshirts.xlToXxxxl.sizes.XXXL || 0;
        }
      }
      if (order.stickers) {
        stickerRolls += order.stickers.quantity || 0;
      }
    });

    return { smallToLarge, xlToXxxxl, stickerRolls, vneckSizes };
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateShort = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  };

  // ─── CSV EXPORTS ──────────────────────────────────────────────────

  const getBatchLabel = () => {
    if (selectedBatch === 'all') return 'All Orders';
    const batch = batches.find(b => b.id === selectedBatch);
    return batch ? batch.name : 'Unknown Batch';
  };

  const exportSupplierOrder = () => {
    const filtered = getFilteredOrders();
    const sizeSummary = getSizeSummary(filtered);
    const adultSummary = getAdultSummary(filtered);

    const batchLabel = getBatchLabel();
    const filterLabel = filterFranchisee === 'all' ? 'All Franchisees' : filterFranchisee;
    const dateStr = new Date().toLocaleDateString('en-ZA');

    let csv = `BEAUX-ARTS KIDZ KRAFTZ - T-SHIRT ORDER FOR SUPPLIER\n`;
    csv += `Generated: ${dateStr}\n`;
    csv += `Supplier Batch: ${batchLabel}\n`;
    csv += `Franchisee Filter: ${filterLabel}\n\n`;

    csv += `SIZE SUMMARY (for supplier order)\n`;
    csv += `Size,Quantity\n`;
    allSizes.forEach(size => {
      if (sizeSummary[size] > 0) {
        csv += `${size},${sizeSummary[size]}\n`;
      }
    });

    const totalKids = allSizes.slice(0, 7).reduce((sum, s) => sum + sizeSummary[s], 0);
    const totalAdultRound = allSizes.slice(7, 10).reduce((sum, s) => sum + sizeSummary[s], 0);
    csv += `\nTotal Kids T-shirts (round neck),${totalKids}\n`;
    csv += `Total Adult Round Neck (S/M/L),${totalAdultRound}\n`;

    if (adultSummary.smallToLarge > 0 || adultSummary.xlToXxxxl > 0) {
      csv += `Adult V-neck (S-L),${adultSummary.smallToLarge}\n`;
      csv += `Adult V-neck (XL-XXXL),${adultSummary.xlToXxxxl}\n`;
    }
    if (adultSummary.stickerRolls > 0) {
      csv += `Sticker Rolls,${adultSummary.stickerRolls}\n`;
    }

    csv += `\n\nDETAILED BREAKDOWN BY FRANCHISEE\n`;
    csv += `Franchisee,Order #,Order Date,School,Student,Size,In Month-End\n`;

    filtered.forEach(order => {
      if (order.studentTshirts && Array.isArray(order.studentTshirts)) {
        order.studentTshirts.forEach(st => {
          csv += `${order.franchiseeName || 'Unknown'},`;
          csv += `${order.orderNumber || 'N/A'},`;
          csv += `${formatDate(order.orderDateParsed)},`;
          csv += `${st.schoolName || 'N/A'},`;
          csv += `${st.studentName || 'N/A'},`;
          csv += `${st.size || 'N/A'},`;
          csv += `${order.includeInMonthEnd ? 'Yes' : 'No'}\n`;
        });
      }
      if (order.extraStock) {
        Object.entries(order.extraStock).forEach(([size, qty]) => {
          if (qty > 0) {
            csv += `${order.franchiseeName || 'Unknown'},`;
            csv += `${order.orderNumber || 'N/A'},`;
            csv += `${formatDate(order.orderDateParsed)},`;
            csv += `EXTRA STOCK,,`;
            csv += `${size} x${qty},`;
            csv += `${order.includeInMonthEnd ? 'Yes' : 'No'}\n`;
          }
        });
      }
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TShirtOrder_${batchLabel.replace(/\s+/g, '_')}_${dateStr}.csv`;
    a.click();
  };

  const exportDetailedReport = () => {
    const filtered = getFilteredOrders();
    const dateStr = new Date().toLocaleDateString('en-ZA');
    const batchLabel = getBatchLabel();

    let csv = `BEAUX-ARTS KIDZ KRAFTZ - T-SHIRT ORDERS DETAILED REPORT\n`;
    csv += `Generated: ${dateStr}\n`;
    csv += `Supplier Batch: ${batchLabel}\n\n`;

    csv += `Franchisee,Order #,Order Date,Student Shirts,Extra Stock,Adult Shirts,Stickers,`;
    csv += `Total Cost,Cost to HO,In Month-End,Status\n`;

    filtered.forEach(order => {
      csv += `${order.franchiseeName || 'Unknown'},`;
      csv += `${order.orderNumber || 'N/A'},`;
      csv += `${formatDate(order.orderDateParsed)},`;
      csv += `${order.studentTshirtCount || 0},`;
      csv += `${order.extraStockCount || 0},`;
      csv += `${(order.adultTshirts?.smallToLarge?.quantity || 0) + (order.adultTshirts?.xlToXxxxl?.quantity || 0)},`;
      csv += `${order.stickers?.quantity || 0},`;
      csv += `R${(order.totalCost || 0).toFixed(2)},`;
      csv += `R${(order.totalCostToHO || 0).toFixed(2)},`;
      csv += `${order.includeInMonthEnd ? 'Yes' : 'No'},`;
      csv += `${order.status || 'N/A'}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TShirtOrders_Report_${batchLabel.replace(/\s+/g, '_')}_${dateStr}.csv`;
    a.click();
  };

  // ─── FRANCHISEE VERIFICATION REPORT ───────────────────────────────

  const exportFranchiseeVerification = () => {
    const filtered = getFilteredOrders();
    const dateStr = new Date().toLocaleDateString('en-ZA');
    const batchLabel = getBatchLabel();

    // Use headers that Excel won't convert to dates
    const kidsSizeHeaders = ['2-3 Yrs', '4-5 Yrs', '5-6 Yrs', '7-8 Yrs', '9-10 Yrs', '11-12 Yrs', '13-14 Yrs'];
    const kidsFullNames = ['2-3 YEARS', '4-5 YEARS', '5-6 YEARS', '7-8 YEARS', '9-10 YEARS', '11-12 YEARS', '13-14 YEARS'];
    const roundNeckAdult = ['S', 'M', 'L'];
    const vneckHeaders = ['V-S', 'V-M', 'V-L', 'V-XL', 'V-XXL', 'V-XXXL'];

    let csv = `BEAUX-ARTS KIDZ KRAFTZ - FRANCHISEE VERIFICATION REPORT\n`;
    csv += `Generated: ${dateStr}\n`;
    csv += `Supplier Batch: ${batchLabel}\n`;
    csv += `Note: Size columns include student shirts + extra stock combined\n\n`;

    // Column headers
    csv += `Franchisee,Order #,Order Date,`;
    csv += kidsSizeHeaders.join(',') + ',';
    csv += roundNeckAdult.join(',') + ',';
    csv += vneckHeaders.join(',') + ',';
    csv += `Stickers,Total Shirts,Total Cost,Cost to HO\n`;

    // Sort orders by franchisee name then date
    const sorted = [...filtered].sort((a, b) => {
      const nameCmp = (a.franchiseeName || '').localeCompare(b.franchiseeName || '');
      if (nameCmp !== 0) return nameCmp;
      return (a.orderDateParsed || new Date(0)) - (b.orderDateParsed || new Date(0));
    });

    // Helper to normalize size keys for matching
    const normalizeSize = (size) => {
      if (!size) return '';
      return size.toUpperCase().trim();
    };

    sorted.forEach(order => {
      // Count ALL shirts by size (students + extra stock combined)
      const sizeCounts = {};
      
      // Student shirts
      (order.studentTshirts || []).forEach(st => {
        if (st.size) {
          const key = normalizeSize(st.size);
          sizeCounts[key] = (sizeCounts[key] || 0) + 1;
        }
      });
      
      // Extra stock — add to the same size buckets
      if (order.extraStock) {
        Object.entries(order.extraStock).forEach(([size, qty]) => {
          if (qty > 0) {
            const key = normalizeSize(size);
            sizeCounts[key] = (sizeCounts[key] || 0) + qty;
          }
        });
      }

      // V-neck sizes
      const vneckCounts = { 'V-S': 0, 'V-M': 0, 'V-L': 0, 'V-XL': 0, 'V-XXL': 0, 'V-XXXL': 0 };
      if (order.adultTshirts?.smallToLarge?.sizes) {
        vneckCounts['V-S'] = order.adultTshirts.smallToLarge.sizes.S || 0;
        vneckCounts['V-M'] = order.adultTshirts.smallToLarge.sizes.M || 0;
        vneckCounts['V-L'] = order.adultTshirts.smallToLarge.sizes.L || 0;
      }
      if (order.adultTshirts?.xlToXxxxl?.sizes) {
        vneckCounts['V-XL'] = order.adultTshirts.xlToXxxxl.sizes.XL || 0;
        vneckCounts['V-XXL'] = order.adultTshirts.xlToXxxxl.sizes.XXL || 0;
        vneckCounts['V-XXXL'] = order.adultTshirts.xlToXxxxl.sizes.XXXL || 0;
      }

      // Calculate total round neck shirts across all sizes
      let totalRoundNeck = 0;
      kidsFullNames.forEach(size => { totalRoundNeck += sizeCounts[normalizeSize(size)] || 0; });
      roundNeckAdult.forEach(size => { totalRoundNeck += sizeCounts[normalizeSize(size)] || 0; });
      const totalVneck = Object.values(vneckCounts).reduce((s, v) => s + v, 0);

      // Build row
      csv += `${order.franchiseeName || 'Unknown'},`;
      csv += `${order.orderNumber || 'N/A'},`;
      csv += `${formatDate(order.orderDateParsed)},`;

      // Kids sizes
      kidsFullNames.forEach(size => {
        csv += `${sizeCounts[normalizeSize(size)] || 0},`;
      });

      // Adult round neck S/M/L
      roundNeckAdult.forEach(size => {
        csv += `${sizeCounts[normalizeSize(size)] || 0},`;
      });

      // V-neck sizes
      vneckHeaders.forEach(size => {
        csv += `${vneckCounts[size] || 0},`;
      });

      // Stickers, total, costs
      csv += `${order.stickers?.quantity || 0},`;
      csv += `${totalRoundNeck + totalVneck},`;
      csv += `R${(order.totalCost || 0).toFixed(2)},`;
      csv += `R${(order.totalCostToHO || 0).toFixed(2)}\n`;
    });

    // Totals row
    csv += `\nTOTALS,,,`;

    const grandSizes = {};
    const grandVneck = { 'V-S': 0, 'V-M': 0, 'V-L': 0, 'V-XL': 0, 'V-XXL': 0, 'V-XXXL': 0 };
    let grandStickers = 0;
    let grandTotal = 0;
    let grandCost = 0;
    let grandCostHO = 0;

    sorted.forEach(order => {
      (order.studentTshirts || []).forEach(st => {
        if (st.size) {
          const key = normalizeSize(st.size);
          grandSizes[key] = (grandSizes[key] || 0) + 1;
        }
      });
      if (order.extraStock) {
        Object.entries(order.extraStock).forEach(([size, qty]) => {
          if (qty > 0) {
            const key = normalizeSize(size);
            grandSizes[key] = (grandSizes[key] || 0) + qty;
          }
        });
      }
      if (order.adultTshirts?.smallToLarge?.sizes) {
        grandVneck['V-S'] += order.adultTshirts.smallToLarge.sizes.S || 0;
        grandVneck['V-M'] += order.adultTshirts.smallToLarge.sizes.M || 0;
        grandVneck['V-L'] += order.adultTshirts.smallToLarge.sizes.L || 0;
      }
      if (order.adultTshirts?.xlToXxxxl?.sizes) {
        grandVneck['V-XL'] += order.adultTshirts.xlToXxxxl.sizes.XL || 0;
        grandVneck['V-XXL'] += order.adultTshirts.xlToXxxxl.sizes.XXL || 0;
        grandVneck['V-XXXL'] += order.adultTshirts.xlToXxxxl.sizes.XXXL || 0;
      }
      grandStickers += order.stickers?.quantity || 0;
      grandCost += order.totalCost || 0;
      grandCostHO += order.totalCostToHO || 0;
    });

    kidsFullNames.forEach(size => { 
      const val = grandSizes[normalizeSize(size)] || 0;
      csv += `${val},`;
      grandTotal += val;
    });
    roundNeckAdult.forEach(size => { 
      const val = grandSizes[normalizeSize(size)] || 0;
      csv += `${val},`;
      grandTotal += val;
    });
    vneckHeaders.forEach(size => { 
      const val = grandVneck[size] || 0;
      csv += `${val},`;
      grandTotal += val;
    });
    csv += `${grandStickers},`;
    csv += `${grandTotal},`;
    csv += `R${grandCost.toFixed(2)},`;
    csv += `R${grandCostHO.toFixed(2)}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Franchisee_Verification_${batchLabel.replace(/\s+/g, '_')}_${dateStr}.csv`;
    a.click();
  };

  // ─── PACKING REPORT (multi-batch, grouped by franchisee) ─────────

  const handleOpenPackingReport = () => {
    // Pre-select all batches
    setSelectedPackingBatches(batches.map(b => b.id));
    setShowPackingModal(true);
  };

  const togglePackingBatch = (batchId) => {
    setSelectedPackingBatches(prev => 
      prev.includes(batchId) 
        ? prev.filter(id => id !== batchId) 
        : [...prev, batchId]
    );
  };

  const exportPackingReport = () => {
    // Get orders from all selected batches
    const packingOrders = orders.filter(order => {
      return selectedPackingBatches.some(batchId => {
        const batch = batches.find(b => b.id === batchId);
        return batch && isOrderInBatch(order, batch);
      });
    });

    if (packingOrders.length === 0) {
      alert('No orders found for the selected batches.');
      return;
    }

    const dateStr = new Date().toLocaleDateString('en-ZA');
    const selectedBatchNames = selectedPackingBatches
      .map(id => batches.find(b => b.id === id)?.name || '')
      .filter(Boolean)
      .join(' + ');

    const kidsFullNames = ['2-3 YEARS', '4-5 YEARS', '5-6 YEARS', '7-8 YEARS', '9-10 YEARS', '11-12 YEARS', '13-14 YEARS'];
    const kidsSizeHeaders = ['2-3 Yrs', '4-5 Yrs', '5-6 Yrs', '7-8 Yrs', '9-10 Yrs', '11-12 Yrs', '13-14 Yrs'];
    const roundNeckAdult = ['S', 'M', 'L'];
    const vneckHeaders = ['V-S', 'V-M', 'V-L', 'V-XL', 'V-XXL', 'V-XXXL'];

    const normalizeSize = (size) => {
      if (!size) return '';
      return size.toUpperCase().trim();
    };

    // Group orders by franchisee
    const byFranchisee = {};
    packingOrders.forEach(order => {
      const name = order.franchiseeName || 'Unknown';
      if (!byFranchisee[name]) byFranchisee[name] = [];
      byFranchisee[name].push(order);
    });

    const sortedNames = Object.keys(byFranchisee).sort();

    let csv = `BEAUX-ARTS KIDZ KRAFTZ - PACKING REPORT FOR PRINTER\n`;
    csv += `Generated: ${dateStr}\n`;
    csv += `Batches Included: ${selectedBatchNames}\n`;
    csv += `Total Orders: ${packingOrders.length} | Franchisees: ${sortedNames.length}\n\n`;

    // Column headers
    csv += `Franchisee,Orders,`;
    csv += kidsSizeHeaders.join(',') + ',';
    csv += roundNeckAdult.join(',') + ',';
    csv += `Total Round Neck,`;
    csv += vneckHeaders.join(',') + ',';
    csv += `Total V-Neck,Stickers,TOTAL SHIRTS\n`;

    // Grand totals tracking
    const grandSizes = {};
    const grandVneck = { 'V-S': 0, 'V-M': 0, 'V-L': 0, 'V-XL': 0, 'V-XXL': 0, 'V-XXXL': 0 };
    let grandStickers = 0;

    sortedNames.forEach(franchiseeName => {
      const franchiseeOrders = byFranchisee[franchiseeName];

      // Combine all sizes across this franchisee's orders
      const sizeCounts = {};
      const vneckCounts = { 'V-S': 0, 'V-M': 0, 'V-L': 0, 'V-XL': 0, 'V-XXL': 0, 'V-XXXL': 0 };
      let stickerTotal = 0;

      franchiseeOrders.forEach(order => {
        // Student shirts
        (order.studentTshirts || []).forEach(st => {
          if (st.size) {
            const key = normalizeSize(st.size);
            sizeCounts[key] = (sizeCounts[key] || 0) + 1;
          }
        });

        // Extra stock
        if (order.extraStock) {
          Object.entries(order.extraStock).forEach(([size, qty]) => {
            if (qty > 0) {
              const key = normalizeSize(size);
              sizeCounts[key] = (sizeCounts[key] || 0) + qty;
            }
          });
        }

        // V-necks
        if (order.adultTshirts?.smallToLarge?.sizes) {
          vneckCounts['V-S'] += order.adultTshirts.smallToLarge.sizes.S || 0;
          vneckCounts['V-M'] += order.adultTshirts.smallToLarge.sizes.M || 0;
          vneckCounts['V-L'] += order.adultTshirts.smallToLarge.sizes.L || 0;
        }
        if (order.adultTshirts?.xlToXxxxl?.sizes) {
          vneckCounts['V-XL'] += order.adultTshirts.xlToXxxxl.sizes.XL || 0;
          vneckCounts['V-XXL'] += order.adultTshirts.xlToXxxxl.sizes.XXL || 0;
          vneckCounts['V-XXXL'] += order.adultTshirts.xlToXxxxl.sizes.XXXL || 0;
        }

        // Stickers
        stickerTotal += order.stickers?.quantity || 0;
      });

      // Calculate row totals
      let totalRoundNeck = 0;
      kidsFullNames.forEach(size => { totalRoundNeck += sizeCounts[normalizeSize(size)] || 0; });
      roundNeckAdult.forEach(size => { totalRoundNeck += sizeCounts[normalizeSize(size)] || 0; });
      const totalVneck = Object.values(vneckCounts).reduce((s, v) => s + v, 0);

      // Build row
      csv += `${franchiseeName},${franchiseeOrders.length},`;

      kidsFullNames.forEach(size => { csv += `${sizeCounts[normalizeSize(size)] || 0},`; });
      roundNeckAdult.forEach(size => { csv += `${sizeCounts[normalizeSize(size)] || 0},`; });
      csv += `${totalRoundNeck},`;
      vneckHeaders.forEach(size => { csv += `${vneckCounts[size] || 0},`; });
      csv += `${totalVneck},`;
      csv += `${stickerTotal},`;
      csv += `${totalRoundNeck + totalVneck}\n`;

      // Add to grand totals
      kidsFullNames.forEach(size => { 
        const key = normalizeSize(size);
        grandSizes[key] = (grandSizes[key] || 0) + (sizeCounts[key] || 0); 
      });
      roundNeckAdult.forEach(size => { 
        const key = normalizeSize(size);
        grandSizes[key] = (grandSizes[key] || 0) + (sizeCounts[key] || 0); 
      });
      vneckHeaders.forEach(size => { grandVneck[size] += vneckCounts[size] || 0; });
      grandStickers += stickerTotal;
    });

    // Grand totals row
    let grandRoundNeck = 0;
    csv += `\nTOTALS,,`;
    kidsFullNames.forEach(size => { 
      const val = grandSizes[normalizeSize(size)] || 0;
      csv += `${val},`; 
      grandRoundNeck += val;
    });
    roundNeckAdult.forEach(size => { 
      const val = grandSizes[normalizeSize(size)] || 0;
      csv += `${val},`; 
      grandRoundNeck += val;
    });
    csv += `${grandRoundNeck},`;
    const grandVneckTotal = Object.values(grandVneck).reduce((s, v) => s + v, 0);
    vneckHeaders.forEach(size => { csv += `${grandVneck[size] || 0},`; });
    csv += `${grandVneckTotal},`;
    csv += `${grandStickers},`;
    csv += `${grandRoundNeck + grandVneckTotal}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Packing_Report_${dateStr}.csv`;
    a.click();

    setShowPackingModal(false);
  };

  // ─── PDF EXPORT ───────────────────────────────────────────────────

  const exportSupplierPDF = async () => {
    const filtered = getFilteredOrders();
    const sizeSummaryData = getSizeSummary(filtered);
    const adultSummaryData = getAdultSummary(filtered);
    const batchLabel = getBatchLabel();
    const filterLabel = filterFranchisee === 'all' ? 'All Franchisees' : filterFranchisee;
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

    const selectedBatchObj = batches.find(b => b.id === selectedBatch);
    const orderRef = selectedBatchObj
      ? `BAKK-${selectedBatchObj.name.replace(/\s+/g, '-')}`
      : `BAKK-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}-${String(filtered.length).padStart(2, '0')}`;

    const doc2 = new jsPDF();
    let yPos = 15;

    // Load logo
    let logoLoaded = false;
    try {
      const logoImg = new Image();
      logoImg.src = '/arty.png';
      await new Promise((resolve) => {
        logoImg.onload = () => {
          doc2.addImage(logoImg, 'PNG', 14, yPos, 28, 28);
          logoLoaded = true;
          resolve();
        };
        logoImg.onerror = () => resolve();
        setTimeout(resolve, 2000);
      });
    } catch (error) {
      console.log('Logo not loaded, continuing without it');
    }

    // Header
    const headerX = logoLoaded ? 48 : 14;
    doc2.setFontSize(20);
    doc2.setFont(undefined, 'bold');
    doc2.setTextColor(51, 51, 51);
    doc2.text('Beaux-Arts Kidz Kraftz', headerX, yPos + 10);

    doc2.setFontSize(11);
    doc2.setFont(undefined, 'normal');
    doc2.setTextColor(100, 100, 100);
    doc2.text('Linda Lloyd  |  info@bacreate.com  |  082 4442223', headerX, yPos + 18);

    yPos += 35;

    // Purple accent line
    doc2.setDrawColor(128, 0, 128);
    doc2.setLineWidth(1.5);
    doc2.line(14, yPos, 196, yPos);
    yPos += 10;

    // Title
    doc2.setFontSize(18);
    doc2.setFont(undefined, 'bold');
    doc2.setTextColor(51, 51, 51);
    doc2.text('T-SHIRT ORDER', 14, yPos);
    yPos += 10;

    // Order details row
    doc2.setFontSize(10);
    doc2.setFont(undefined, 'normal');
    doc2.setTextColor(80, 80, 80);
    doc2.text(`Order Ref: ${orderRef}`, 14, yPos);
    doc2.text(`Date: ${dateStr}`, 105, yPos);
    yPos += 6;
    doc2.text(`Supplier Batch: ${batchLabel}`, 14, yPos);
    doc2.text(`Total Orders: ${filtered.length}`, 105, yPos);
    yPos += 6;
    if (selectedBatchObj) {
      doc2.text(`Period: ${formatDateShort(selectedBatchObj.startDateParsed)} – ${formatDateShort(selectedBatchObj.endDateParsed)}`, 14, yPos);
    }
    doc2.text(`Franchisee: ${filterLabel}`, 105, yPos);
    yPos += 12;

    // Kids T-shirts section
    doc2.setFontSize(13);
    doc2.setFont(undefined, 'bold');
    doc2.setTextColor(51, 51, 51);
    doc2.text('Kids T-shirts', 14, yPos);
    doc2.setFontSize(9);
    doc2.setFont(undefined, 'normal');
    doc2.setTextColor(120, 120, 120);
    doc2.text('All black and round neck', 14, yPos + 5);
    yPos += 12;

    const kidsSizes = ['2-3 YEARS', '4-5 YEARS', '5-6 YEARS', '7-8 YEARS', '9-10 YEARS', '11-12 YEARS', '13-14 YEARS'];
    const kidsData = [];
    for (let i = 0; i < kidsSizes.length; i += 2) {
      const row = [kidsSizes[i], sizeSummaryData[kidsSizes[i]] || 0];
      if (i + 1 < kidsSizes.length) {
        row.push(kidsSizes[i + 1]);
        row.push(sizeSummaryData[kidsSizes[i + 1]] || 0);
      } else {
        row.push('');
        row.push('');
      }
      kidsData.push(row);
    }

    autoTable(doc2, {
      startY: yPos,
      head: [['Size', 'Qty', 'Size', 'Qty']],
      body: kidsData,
      theme: 'grid',
      styles: { fontSize: 11, cellPadding: 6 },
      headStyles: { fillColor: [128, 0, 128], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 25, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 45 },
        3: { cellWidth: 25, halign: 'center', fontStyle: 'bold' }
      },
      margin: { left: 14, right: 14 }
    });

    yPos = doc2.lastAutoTable.finalY + 10;

    // Round neck adult sizes
    const roundNeckSizes = ['S', 'M', 'L'];
    doc2.setFontSize(13);
    doc2.setFont(undefined, 'bold');
    doc2.setTextColor(51, 51, 51);
    doc2.text('Adult Round Neck T-shirts', 14, yPos);
    yPos += 8;

    const roundNeckData = [];
    for (let i = 0; i < roundNeckSizes.length; i += 2) {
      const row = [roundNeckSizes[i] + ' round neck', sizeSummaryData[roundNeckSizes[i]] || 0];
      if (i + 1 < roundNeckSizes.length) {
        row.push(roundNeckSizes[i + 1] + ' round neck');
        row.push(sizeSummaryData[roundNeckSizes[i + 1]] || 0);
      } else {
        row.push('');
        row.push('');
      }
      roundNeckData.push(row);
    }

    autoTable(doc2, {
      startY: yPos,
      head: [['Size', 'Qty', 'Size', 'Qty']],
      body: roundNeckData,
      theme: 'grid',
      styles: { fontSize: 11, cellPadding: 6 },
      headStyles: { fillColor: [128, 0, 128], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 25, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 45 },
        3: { cellWidth: 25, halign: 'center', fontStyle: 'bold' }
      },
      margin: { left: 14, right: 14 }
    });

    yPos = doc2.lastAutoTable.finalY + 10;

    // V-neck adult sizes
    if (adultSummaryData.smallToLarge > 0 || adultSummaryData.xlToXxxxl > 0) {
      doc2.setFontSize(13);
      doc2.setFont(undefined, 'bold');
      doc2.text('Adult V-neck T-shirts', 14, yPos);
      doc2.setFontSize(9);
      doc2.setFont(undefined, 'normal');
      doc2.setTextColor(120, 120, 120);
      doc2.text('Black V-neck', 14, yPos + 5);
      yPos += 12;

      const vneckAllSizes = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
      const vneckWithQty = vneckAllSizes.filter(s => adultSummaryData.vneckSizes[s] > 0);

      const vneckData = [];
      for (let i = 0; i < vneckWithQty.length; i += 2) {
        const row = [vneckWithQty[i] + ' V-neck', adultSummaryData.vneckSizes[vneckWithQty[i]]];
        if (i + 1 < vneckWithQty.length) {
          row.push(vneckWithQty[i + 1] + ' V-neck');
          row.push(adultSummaryData.vneckSizes[vneckWithQty[i + 1]]);
        } else {
          row.push('');
          row.push('');
        }
        vneckData.push(row);
      }

      if (vneckData.length === 0) {
        vneckData.push(['S to L', adultSummaryData.smallToLarge, 'XL to XXXL', adultSummaryData.xlToXxxxl]);
      }

      autoTable(doc2, {
        startY: yPos,
        head: [['Size', 'Qty', 'Size', 'Qty']],
        body: vneckData,
        theme: 'grid',
        styles: { fontSize: 11, cellPadding: 6 },
        headStyles: { fillColor: [128, 0, 128], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 25, halign: 'center', fontStyle: 'bold' },
          2: { cellWidth: 45 },
          3: { cellWidth: 25, halign: 'center', fontStyle: 'bold' }
        },
        margin: { left: 14, right: 14 }
      });

      yPos = doc2.lastAutoTable.finalY + 10;
    }

    // Totals
    const totalKids = kidsSizes.reduce((sum, s) => sum + (sizeSummaryData[s] || 0), 0);
    const totalRoundNeck = roundNeckSizes.reduce((sum, s) => sum + (sizeSummaryData[s] || 0), 0);
    const totalAll = totalKids + totalRoundNeck + adultSummaryData.smallToLarge + adultSummaryData.xlToXxxxl;

    autoTable(doc2, {
      startY: yPos,
      body: [
        ['Total Kids Round Neck T-shirts:', String(totalKids)],
        ['Total Adult Round Neck T-shirts:', String(totalRoundNeck)],
        ['Total V-neck T-shirts:', String(adultSummaryData.smallToLarge + adultSummaryData.xlToXxxxl)],
        ['GRAND TOTAL T-SHIRTS:', String(totalAll)]
      ],
      theme: 'plain',
      styles: { fontSize: 11, cellPadding: 4 },
      columnStyles: {
        0: { cellWidth: 80, fontStyle: 'bold' },
        1: { cellWidth: 30, halign: 'center', fontStyle: 'bold', fontSize: 13 }
      },
      didParseCell: function(data) {
        if (data.row.index === 3) {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontSize = 13;
        }
      },
      margin: { left: 14, right: 14 }
    });

    doc2.save(`TShirt_Supplier_Order_${orderRef}.pdf`);
  };

  // ─── RENDER ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="ho-tshirt-container">
        <div className="loading-spinner">Loading T-shirt orders...</div>
      </div>
    );
  }

  const filteredOrders = getFilteredOrders();
  const sizeSummary = getSizeSummary(filteredOrders);
  const adultSummary = getAdultSummary(filteredOrders);
  const totalStudentShirts = Object.values(sizeSummary).reduce((a, b) => a + b, 0);
  const totalCostToHO = filteredOrders.reduce((sum, o) => sum + (o.totalCostToHO || 0), 0);
  const { counts: batchCounts } = { counts: getBatchOrderCounts() };

  return (
    <div className="ho-tshirt-container">
      {/* Header */}
      <div className="ho-tshirt-header">
        <h1>👕 T-Shirt Orders</h1>
        <p className="subtitle">View all franchisee t-shirt orders</p>
      </div>

      {/* ── SUPPLIER BATCH SECTION ── */}
      <div className="batch-section-card">
        <div className="batch-section-header">
          <h2>📦 Supplier Order Batches</h2>
          <button onClick={handleCreateBatch} className="batch-add-btn">+ New Batch</button>
        </div>

        {batches.length === 0 ? (
          <div className="batch-empty-state">
            <p>No supplier batches created yet. Create your first batch to group orders by week.</p>
          </div>
        ) : (
          <div className="batch-cards-row">
            {/* All Orders card */}
            <div
              className={`batch-card ${selectedBatch === 'all' ? 'batch-card-selected' : ''}`}
              onClick={() => setSelectedBatch('all')}
            >
              <div className="batch-card-name">All Orders</div>
              <div className="batch-card-count">{orders.length} orders</div>
            </div>

            {/* Individual batch cards */}
            {batches.map(batch => {
              const count = batchCounts[batch.id] || 0;
              const isSelected = selectedBatch === batch.id;
              const isOrdered = batch.status === 'ordered';

              return (
                <div
                  key={batch.id}
                  className={`batch-card ${isSelected ? 'batch-card-selected' : ''} ${isOrdered ? 'batch-card-ordered' : ''}`}
                  onClick={() => setSelectedBatch(batch.id)}
                >
                  {isOrdered && <div className="batch-ordered-ribbon">✓ Ordered</div>}
                  <div className="batch-card-name">{batch.name}</div>
                  <div className="batch-card-dates">
                    {formatDateShort(batch.startDateParsed)} – {formatDateShort(batch.endDateParsed)}
                  </div>
                  <div className="batch-card-count">{count} orders</div>
                  {isSelected && (
                    <div className="batch-card-actions">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggleBatchStatus(batch); }}
                        className={`batch-status-btn ${isOrdered ? 'reopen' : 'mark-ordered'}`}
                        title={isOrdered ? 'Reopen batch' : 'Mark as ordered from supplier'}
                      >
                        {isOrdered ? '↩ Reopen' : '✓ Mark Ordered'}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditBatch(batch); }}
                        className="batch-edit-btn"
                        title="Edit batch"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteBatch(batch.id); }}
                        className="batch-delete-btn"
                        title="Delete batch"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {selectedBatch !== 'all' && (
          <div className="batch-active-banner">
            Showing orders for: <strong>{getBatchLabel()}</strong>
            {batches.find(b => b.id === selectedBatch) && (
              <span className="batch-active-dates">
                ({formatDateShort(batches.find(b => b.id === selectedBatch).startDateParsed)} – {formatDateShort(batches.find(b => b.id === selectedBatch).endDateParsed)})
              </span>
            )}
            <button onClick={() => setSelectedBatch('all')} className="batch-clear-btn">✕ Show All</button>
          </div>
        )}
      </div>

      {/* ── BATCH MODAL ── */}
      {showBatchModal && (
        <div className="batch-modal-overlay" onClick={() => setShowBatchModal(false)}>
          <div className="batch-modal" onClick={(e) => e.stopPropagation()}>
            <div className="batch-modal-header">
              <h2>{editingBatch ? 'Edit Batch' : 'Create Supplier Batch'}</h2>
              <button onClick={() => setShowBatchModal(false)} className="batch-modal-close">✕</button>
            </div>

            <div className="batch-modal-body">
              <div className="batch-form-group">
                <label>Batch Name</label>
                <input
                  type="text"
                  value={batchForm.name}
                  onChange={(e) => setBatchForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Supplier Order 1"
                  className="batch-form-input"
                />
              </div>

              <div className="batch-form-row">
                <div className="batch-form-group">
                  <label>Start Date (orders from)</label>
                  <input
                    type="date"
                    value={batchForm.startDate}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className="batch-form-input"
                  />
                </div>
                <div className="batch-form-group">
                  <label>End Date (orders up to)</label>
                  <input
                    type="date"
                    value={batchForm.endDate}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, endDate: e.target.value }))}
                    className="batch-form-input"
                  />
                </div>
              </div>

              <p className="batch-form-help">
                All franchisee orders placed between these dates will be grouped into this batch. 
                Orders that don't fall into any batch will auto-join the latest batch.
              </p>
            </div>

            <div className="batch-modal-footer">
              <button onClick={() => setShowBatchModal(false)} className="batch-modal-cancel" disabled={savingBatch}>
                Cancel
              </button>
              <button onClick={handleSaveBatch} className="batch-modal-save" disabled={savingBatch}>
                {savingBatch ? 'Saving...' : editingBatch ? 'Update Batch' : 'Create Batch'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PACKING REPORT MODAL ── */}
      {showPackingModal && (
        <div className="batch-modal-overlay" onClick={() => setShowPackingModal(false)}>
          <div className="batch-modal" onClick={(e) => e.stopPropagation()}>
            <div className="batch-modal-header" style={{ background: 'linear-gradient(135deg, #FF6F00, #E65100)' }}>
              <h2>📦 Packing Report</h2>
              <button onClick={() => setShowPackingModal(false)} className="batch-modal-close">✕</button>
            </div>

            <div className="batch-modal-body">
              <p style={{ margin: '0 0 16px', color: '#555', fontSize: '14px' }}>
                Select which supplier orders to include. The report will combine all selected batches 
                and show totals per franchisee with size breakdowns — ready for your printer to pack.
              </p>

              <div className="packing-batch-list">
                {batches.map(batch => {
                  const isChecked = selectedPackingBatches.includes(batch.id);
                  const count = orders.filter(o => isOrderInBatch(o, batch)).length;
                  return (
                    <label key={batch.id} className={`packing-batch-item ${isChecked ? 'checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => togglePackingBatch(batch.id)}
                      />
                      <div className="packing-batch-info">
                        <span className="packing-batch-name">{batch.name}</span>
                        <span className="packing-batch-dates">
                          {formatDateShort(batch.startDateParsed)} – {formatDateShort(batch.endDateParsed)}
                        </span>
                      </div>
                      <span className="packing-batch-count">{count} orders</span>
                    </label>
                  );
                })}
              </div>

              {selectedPackingBatches.length === 0 && (
                <p style={{ color: '#e53935', fontSize: '13px', marginTop: '10px' }}>
                  Please select at least one batch.
                </p>
              )}
            </div>

            <div className="batch-modal-footer">
              <button onClick={() => setShowPackingModal(false)} className="batch-modal-cancel">
                Cancel
              </button>
              <button 
                onClick={exportPackingReport} 
                className="batch-modal-save" 
                style={{ background: 'linear-gradient(135deg, #FF6F00, #E65100)' }}
                disabled={selectedPackingBatches.length === 0}
              >
                📦 Download Packing Report
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXISTING FILTERS ── */}
      <div className="tshirt-filters-card">
        <div className="filter-row">
          <div className="filter-group">
            <label>Franchisee:</label>
            <select value={filterFranchisee} onChange={(e) => setFilterFranchisee(e.target.value)}>
              <option value="all">All Franchisees</option>
              {franchiseeList.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>In Month-End:</label>
            <select value={filterIncluded} onChange={(e) => setFilterIncluded(e.target.value)}>
              <option value="all">All</option>
              <option value="yes">Included</option>
              <option value="no">Not Included</option>
            </select>
          </div>

          <button onClick={onClose} className="back-btn">← Back</button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="tshirt-stats-row">
        <div className="tshirt-stat-card">
          <div className="stat-label">Total Orders</div>
          <div className="stat-value">{filteredOrders.length}</div>
        </div>
        <div className="tshirt-stat-card">
          <div className="stat-label">Student Shirts</div>
          <div className="stat-value">{filteredOrders.reduce((sum, o) => sum + (o.studentTshirtCount || 0), 0)}</div>
        </div>
        <div className="tshirt-stat-card">
          <div className="stat-label">Extra Stock</div>
          <div className="stat-value">{filteredOrders.reduce((sum, o) => sum + (o.extraStockCount || 0), 0)}</div>
        </div>
        <div className="tshirt-stat-card">
          <div className="stat-label">Total Cost to HO</div>
          <div className="stat-value">R{totalCostToHO.toFixed(2)}</div>
        </div>
      </div>

      {/* Size Summary */}
      <div className="size-summary-card">
        <h2>📊 Size Summary {selectedBatch !== 'all' ? `(${getBatchLabel()})` : '(All Filtered Orders)'}</h2>
        <p className="summary-note">
          {selectedBatch !== 'all'
            ? `Sizes for this batch only — use this for your supplier order.`
            : `This is the combined total across all filtered orders — use this for your supplier order.`
          }
        </p>
        <div className="size-grid">
          {allSizes.map(size => (
            <div key={size} className={`size-item ${sizeSummary[size] > 0 ? 'has-stock' : ''}`}>
              <span className="size-label">{size}</span>
              <span className="size-count">{sizeSummary[size]}</span>
            </div>
          ))}
        </div>

        {(adultSummary.smallToLarge > 0 || adultSummary.xlToXxxxl > 0 || adultSummary.stickerRolls > 0) && (
          <div className="extras-row">
            {adultSummary.smallToLarge > 0 && (
              <div className="size-item has-stock">
                <span className="size-label">Adult V-neck (S-L)</span>
                <span className="size-count">{adultSummary.smallToLarge}</span>
              </div>
            )}
            {adultSummary.xlToXxxxl > 0 && (
              <div className="size-item has-stock">
                <span className="size-label">Adult V-neck (XL+)</span>
                <span className="size-count">{adultSummary.xlToXxxxl}</span>
              </div>
            )}
            {adultSummary.stickerRolls > 0 && (
              <div className="size-item has-stock">
                <span className="size-label">Sticker Rolls</span>
                <span className="size-count">{adultSummary.stickerRolls}</span>
              </div>
            )}
          </div>
        )}

        <div className="size-total">
          Total Shirts: <strong>{totalStudentShirts + adultSummary.smallToLarge + adultSummary.xlToXxxxl}</strong>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="export-buttons-row">
        <button onClick={exportSupplierPDF} className="export-btn pdf-btn">
          📥 Download Supplier Order (PDF)
        </button>
        <button onClick={exportSupplierOrder} className="export-btn supplier-btn">
          📥 Download Supplier Order (CSV)
        </button>
        <button onClick={exportDetailedReport} className="export-btn report-btn">
          📥 Download Orders Report (CSV)
        </button>
        <button onClick={exportFranchiseeVerification} className="export-btn verify-btn">
          📋 Franchisee Verification Report
        </button>
        <button onClick={handleOpenPackingReport} className="export-btn packing-btn" disabled={batches.length === 0}>
          📦 Packing Report (for Printer)
        </button>
      </div>

      {/* Orders Table */}
      <div className="orders-table-card">
        <h2>📋 Orders ({filteredOrders.length})</h2>

        {filteredOrders.length === 0 ? (
          <div className="empty-state">No t-shirt orders found for the selected filters.</div>
        ) : (
          <div className="orders-list">
            {filteredOrders.map(order => {
              const isExpanded = expandedOrder === order.id;
              const studentCount = order.studentTshirtCount || 0;
              const extraCount = order.extraStockCount || 0;
              const adultCount = (order.adultTshirts?.smallToLarge?.quantity || 0) +
                                (order.adultTshirts?.xlToXxxxl?.quantity || 0);

              // Show which batch this order belongs to
              const orderBatchLabel = getOrderBatchLabel(order);

              return (
                <div key={order.id} className={`order-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="order-header" onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                    <div className="order-info">
                      <span className="order-franchisee">{order.franchiseeName || 'Unknown'}</span>
                      <span className="order-number">#{order.orderNumber || 'N/A'}</span>
                      <span className="order-date">{formatDate(order.orderDateParsed)}</span>
                    </div>
                    <div className="order-counts">
                      <span className="count-badge student-badge">{studentCount} students</span>
                      {extraCount > 0 && <span className="count-badge extra-badge">{extraCount} extra</span>}
                      {adultCount > 0 && <span className="count-badge adult-badge">{adultCount} adult</span>}
                      {selectedBatch === 'all' && orderBatchLabel && (
                        <span className="count-badge batch-badge">{orderBatchLabel}</span>
                      )}
                    </div>
                    <div className="order-meta">
                      <span className={`month-end-badge ${order.includeInMonthEnd ? 'included' : 'not-included'}`}>
                        {order.includeInMonthEnd ? '✅ In Month-End' : '❌ Not in Month-End'}
                      </span>
                      <span className="order-cost">R{(order.totalCostToHO || 0).toFixed(2)}</span>
                      <span className="expand-arrow">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="order-detail">
                      {order.studentTshirts && order.studentTshirts.length > 0 && (
                        <div className="detail-section">
                          <h3>Student T-Shirts ({order.studentTshirts.length})</h3>
                          <table className="detail-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>School</th>
                                <th>Student</th>
                                <th>Size</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.studentTshirts.map((st, idx) => (
                                <tr key={idx}>
                                  <td>{idx + 1}</td>
                                  <td>{st.schoolName || 'N/A'}</td>
                                  <td>{st.studentName || 'N/A'}</td>
                                  <td><span className="size-tag">{st.size || 'N/A'}</span></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {order.extraStock && Object.values(order.extraStock).some(v => v > 0) && (
                        <div className="detail-section">
                          <h3>Extra Stock</h3>
                          <div className="extra-stock-grid">
                            {Object.entries(order.extraStock).map(([size, qty]) => (
                              qty > 0 && (
                                <div key={size} className="extra-stock-item">
                                  <span className="size-label">{size}</span>
                                  <span className="size-count">{qty}</span>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      {(adultCount > 0 || (order.stickers?.quantity || 0) > 0) && (
                        <div className="detail-section">
                          <h3>Adult Shirts & Stickers</h3>
                          <div className="extra-stock-grid">
                            {(order.adultTshirts?.smallToLarge?.quantity || 0) > 0 && (
                              <div className="extra-stock-item">
                                <span className="size-label">V-neck S-L</span>
                                <span className="size-count">{order.adultTshirts.smallToLarge.quantity}</span>
                              </div>
                            )}
                            {(order.adultTshirts?.xlToXxxxl?.quantity || 0) > 0 && (
                              <div className="extra-stock-item">
                                <span className="size-label">V-neck XL+</span>
                                <span className="size-count">{order.adultTshirts.xlToXxxxl.quantity}</span>
                              </div>
                            )}
                            {(order.stickers?.quantity || 0) > 0 && (
                              <div className="extra-stock-item">
                                <span className="size-label">Sticker Rolls</span>
                                <span className="size-count">{order.stickers.quantity}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="detail-section cost-section">
                        <h3>Cost Summary</h3>
                        <div className="cost-grid">
                          <div className="cost-item">
                            <span>Total Cost:</span>
                            <span>R{(order.totalCost || 0).toFixed(2)}</span>
                          </div>
                          <div className="cost-item">
                            <span>Cost to HO:</span>
                            <span className="cost-highlight">R{(order.totalCostToHO || 0).toFixed(2)}</span>
                          </div>
                          {order.notes && (
                            <div className="cost-item notes">
                              <span>Notes:</span>
                              <span>{order.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default HOTshirtOrders;